import React from 'react';
import axios from 'axios';
import get from 'lodash.get';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { defineMessages, FormattedMessage } from 'react-intl';

import { LogService } from '../../../services/log-service';

const messages = defineMessages({
  errorTitle: {
    description: 'Title for a notification that happens when trying to save the seller setting.',
    defaultMessage: 'ShipStation could not save your changes.',
    id: 'C/rguC',
  },
  errorContent: {
    description: 'Description for a notification that happens when trying to save the seller setting.',
    defaultMessage: 'You may not have the correct permissions. Please try again or contact your account administrator.',
    id: 'xfelxN',
  },
});

export const userSettingsBulkUrl = '/user/setting/save/bulk';
export const sellerSettingsBulkUrl = '/seller/settings/save';
export const timezoneUrl = '/seller/updateTimezone';
export const sellerSettingsFeedback = 'seller/settings/feedback';

const stringifyNonStrings = (settingsToSave) => {
  // this is because some values are straight strings, which we want, and some are saved as json strings, which we need to stringify from the normal values we've been using
  const stringified = { ...settingsToSave };
  Object.keys(settingsToSave).forEach((key) => {
    if (typeof settingsToSave[key] !== 'string') {
      stringified[key] = JSON.stringify(settingsToSave[key]);
    }
  });
  return stringified;
};

export const updateUserSettingsBulk = async (settingsToSave) => {
  if (!settingsToSave) {
    throw new Error('No valid user settings to save!');
  }

  const stringified = stringifyNonStrings(settingsToSave);

  try {
    const response = await axios.post(userSettingsBulkUrl, stringified);
    const result = get(response, 'data') || [];
    return result;
  } catch (error) {
    throw new Error(`Error saving user settings: ${error?.message}`);
  }
};

export const updateSellerSettingsBulk = async (settingsToSave) => {
  if (!settingsToSave) {
    throw new Error('No valid seller settings to save!');
  }

  const stringified = stringifyNonStrings(settingsToSave);

  try {
    const response = await axios.post(sellerSettingsBulkUrl, {
      sellerSettings: stringified,
    });
    const result = get(response, 'data.sellerSettings') || {};
    return result;
  } catch (error) {
    if (error?.message?.includes('403')) {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...messages.errorTitle} />,
        content: <FormattedMessage {...messages.errorContent} />,
      });
    }
    throw new Error(`Error saving user settings: ${error?.message}`);
  }
};

export const updateSellerTimeZone = async (timeZone) => {
  if (!timeZone) {
    throw new Error('No valid time zone being saved');
  }

  const { timeZoneId } = timeZone;

  try {
    const response = await axios.post(timezoneUrl, { timeZoneId });
    const result = get(response, 'data.timeZoneIana') || '';
    if (result === '') {
      throw new Error('Did not receive valid timezone');
    }
    return result;
  } catch (error) {
    throw new Error(`Error saving user settings: ${error?.message}`);
  }
};

export const fireAndForgetSellerFeedback = (data) => {
  try {
    axios.post(sellerSettingsFeedback, data);
  } catch (error) {
    LogService.logError(error);
  }
};
